import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Doughnut } from 'react-chartjs-2';
import {
  Chart,
  ArcElement,
  Tooltip,
  Legend
} from 'chart.js';
import '../styles/AdminTopProductsMonthlyChart.css'; // Reutilizamos los estilos existentes

// Registrar los componentes necesarios para la gráfica de pie
Chart.register(
  ArcElement,
  Tooltip,
  Legend
);

const SupervisorSalesDistributionChart = ({ selectedPOS }) => {
  const [chartData, setChartData] = useState({
    labels: [],
    datasets: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalSales, setTotalSales] = useState(0);

  const fetchData = async () => {
    try {
      console.log('=== DEBUG: Iniciando fetchData ===');
      const token = localStorage.getItem('token');
      
      if (!token) {
        setError('No se encontró token de autenticación');
        setLoading(false);
        return;
      }

      const url = selectedPOS && selectedPOS !== 'all' 
        ? `/api/supervisors/sales-distribution?pointOfSaleId=${selectedPOS}`
        : '/api/supervisors/sales-distribution';

      console.log('DEBUG: URL:', url);

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('DEBUG: Response status:', response.status);

      if (!response.ok) {
        throw new Error(`Error en la respuesta del servidor: ${response.status}`);
      }

      const data = await response.json();
      console.log('DEBUG: Response data:', data);
      console.log('DEBUG: Response data structure:', JSON.stringify(data, null, 2));

      if (data.success && data.data) {
        const products = data.data;
        console.log('DEBUG: Products:', products);
        console.log('DEBUG: Products JSON:', JSON.stringify(products, null, 2));
        console.log('DEBUG: Products type:', typeof products);
        console.log('DEBUG: Is array:', Array.isArray(products));
        
        setTotalSales(data.totalSales || 0);
        
        if (!products || !Array.isArray(products) || products.length === 0) {
          console.log('DEBUG: No products or empty array');
          setChartData({ labels: [], datasets: [] });
          setError(null);
          return;
        }

        // Limitar a los primeros 10 productos
        const productLimit = 10;
        const topProducts = products.slice(0, productLimit);
        let otherProductsAmount = 0;
        let otherProductsPercentage = 0;
        
        if (products.length > productLimit) {
          for (let i = productLimit; i < products.length; i++) {
            if (products[i] && products[i].total_amount !== undefined && products[i].percentage !== undefined) {
              otherProductsAmount += parseFloat(products[i].total_amount);
              otherProductsPercentage += parseFloat(products[i].percentage);
            }
          }
        }
        
        console.log('DEBUG: topProducts:', topProducts);
        console.log('DEBUG: First product:', topProducts[0]);
        
        // Preparar los datos para la gráfica
        const labels = topProducts.map(product => {
          console.log('DEBUG: Processing product:', product);
          // Truncar nombres largos para mejor visualización
          const name = product && product.prod_name ? product.prod_name : 'Sin nombre';
          return name && name.length > 15 ? name.substring(0, 15) + '...' : name;
        });
        
        console.log('DEBUG: Labels:', labels);
        
        // Guardar nombres completos para el tooltip
        const fullNames = topProducts.map(product => product && product.prod_name ? product.prod_name : 'Sin nombre');
        
        if (products.length > productLimit) {
          labels.push('Otros');
          fullNames.push('Otros productos');
        }
        
        const amounts = topProducts.map(product => parseFloat(product && product.total_amount ? product.total_amount : 0));
        if (products.length > productLimit) {
          amounts.push(otherProductsAmount);
        }
        
        const percentages = topProducts.map(product => parseFloat(product.percentage));
        if (products.length > productLimit) {
          percentages.push(otherProductsPercentage);
        }
        
        // Generar colores dinámicamente
        const backgroundColors = generateColors(labels.length);
        
        setChartData({
          labels,
          datasets: [
            {
              label: 'Monto de Ventas',
              data: amounts,
              backgroundColor: backgroundColors,
              borderColor: backgroundColors.map(color => color.replace('0.7', '1')),
              borderWidth: 1,
              percentages: percentages, // Guardamos los porcentajes para usarlos en el tooltip
              fullNames: fullNames // Guardamos los nombres completos para el tooltip
            }
          ]
        });
        setError(null);
      } else {
        setChartData({ labels: [], datasets: [] });
        setError('No hay datos disponibles para mostrar');
      }
    } catch (err) {
      console.error('Error en fetchData:', err);
      setChartData({ labels: [], datasets: [] });
      setError(`Error al cargar los datos: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [selectedPOS]);

  // Función para generar colores dinámicamente
  const generateColors = (count) => {
    const baseColors = [
      'rgba(255, 99, 132, 0.7)',
      'rgba(54, 162, 235, 0.7)',
      'rgba(255, 205, 86, 0.7)',
      'rgba(75, 192, 192, 0.7)',
      'rgba(153, 102, 255, 0.7)',
      'rgba(255, 159, 64, 0.7)',
      'rgba(199, 199, 199, 0.7)',
      'rgba(83, 102, 255, 0.7)',
      'rgba(255, 99, 255, 0.7)',
      'rgba(99, 255, 132, 0.7)'
    ];
    
    const colors = [];
    for (let i = 0; i < count; i++) {
      colors.push(baseColors[i % baseColors.length]);
    }
    
    return colors;
  };
  
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: {
        left: 5,
        right: 5,
        top: -10,
        bottom: 20
      }
    },
    aspectRatio: 2, // Cambiar a 2 para hacer la gráfica más ancha que alta
    cutout: '60%',
    radius: '85%',
    rotation: 270, // Rotar para que comience desde arriba
    circumference: 180, // Solo mostrar 180 grados (media dona)
    
    plugins: {
      legend: {
        display: false // Ocultamos la leyenda predeterminada
      },
      tooltip: {
        callbacks: {
          title: function(context) {
            // Mostrar el nombre completo del producto en el título
            const fullName = chartData.datasets[0].fullNames[context[0].dataIndex];
            return fullName;
          },
          label: function(context) {
            const value = context.raw ? context.raw.toLocaleString('es-MX', {
              style: 'currency',
              currency: 'MXN',
              minimumFractionDigits: 2
            }) : '';
            
            // Obtener el porcentaje correspondiente
            const percentage = chartData.datasets[0].percentages[context.dataIndex];
            
            // Solo mostrar el monto y porcentaje (sin repetir el nombre)
            return `${value} (${percentage}%)`;
          }
        }
      }
    }
  };

  return (
    <div className="chart-wrapper">
      <div className="chart-header">
        <h3>Tus Productos o Servicios más Vendidos</h3>
      </div>
      <div className="chart-container">
        {loading ? (
          <div className="loading-container">Cargando datos...</div>
        ) : error ? (
          <div className="error-container">{error}</div>
        ) : chartData.labels.length === 0 ? (
          <div className="no-data-container">No hay datos disponibles para el mes actual</div>
        ) : (
          <>
            <div className="pie-chart-container">
              <div className="pie-chart-wrapper">
                <Doughnut data={chartData} options={options} />
              </div>
            </div>
            
          </>
        )}
      </div>
    </div>
  );
};

export default SupervisorSalesDistributionChart;
